import { useQuery, useMutation } from '@apollo/client';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaImage, FaVideo } from 'react-icons/fa';
import AdminHeader from './AdminHeader.js';
import { ADD_IMG_VID_OPTION, DELETE_IMG_VID_OPTION, GET_IMG_VID_OPTIONS, UPDATE_IMG_VID_OPTION } from '../graphql/Mutation/carousel.js';

const validationSchema = Yup.object({
  image: Yup.mixed().required('Image is required'),
});

const Carousel = () => {
  const { loading, error, data, refetch } = useQuery(GET_IMG_VID_OPTIONS, {
    variables: { page: 1, limit: 10 },
  });

  const [addImgVidOption] = useMutation(ADD_IMG_VID_OPTION);
  const [updateImgVidOption] = useMutation(UPDATE_IMG_VID_OPTION);
  const [deleteImgVidOption] = useMutation(DELETE_IMG_VID_OPTION);

  const formik = useFormik({
    initialValues: {
      image: null,
      video: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await addImgVidOption({ variables: { input: values } });
        refetch();
      } catch (err) {
        console.error('Error adding:', err);
      }
    },
  });

  const handleFileChange = (e, field) => {
    formik.setFieldValue(field, e.currentTarget.files[0]);
  };

  const handleUpdate = async (values) => {
    try {
      await updateImgVidOption({ variables: { input: values } });
      refetch(); // Refetch data after updating
    } catch (err) {
      console.error('Error updating:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteImgVidOption({ variables: { id } });
      refetch(); // Refetch data after deleting
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div>
      {/* <AdminHeader /> */}
      <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 my-10 space-y-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Advertisement Notification
        </h2>

        {/* Form Section */}
        <form onSubmit={formik.handleSubmit} className="p-6 space-y-6 bg-white border border-slate-300 rounded-lg">
          {/* Upload Image */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Upload Image
            </label>
            <div className="flex items-center space-x-3 p-3 border rounded-md bg-slate-50">
              <FaImage className="text-gray-500" />
              <input
                type="file"
                name="image"
                onChange={(e) => handleFileChange(e, "image")}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-semibold file:bg-slate-100 file:hover:bg-slate-200"
              />
            </div>
            {formik.touched.image && formik.errors.image && (
              <p className="text-red-600 text-xs mt-1">{formik.errors.image}</p>
            )}
          </div>

          {/* Upload Video */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Upload Video
            </label>
            <div className="flex items-center space-x-3 p-3 border rounded-md bg-slate-50">
              <FaVideo className="text-gray-500" />
              <input
                type="file"
                name="video"
                onChange={(e) => handleFileChange(e, "video")}
                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-semibold file:bg-slate-100 file:hover:bg-slate-200"
              />
            </div>
            {formik.touched.video && formik.errors.video && (
              <p className="text-red-600 text-xs mt-1">{formik.errors.video}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="w-full py-2.5 rounded-md bg-slate-600 text-white font-semibold text-sm hover:bg-slate-700"
            >
              Upload Carousel
            </button>
          </div>
        </form>

        {/* Uploaded Data Section */}
        <div className="space-y-6">
          {data.getImgVidOption.map((option) => (
            <div key={option.id} className="border p-4 rounded-lg shadow-md bg-white">
              <img src={option.image} alt="Uploaded" className="w-full h-48 object-cover rounded-md" />
              <video src={option.video} controls className="w-full h-48 mt-4 rounded-md" />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => handleUpdate(option)}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                >
                  Update
                </button>
                <button
                  onClick={() => handleDelete(option.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Carousel;
